import { motion } from "framer-motion";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { useSeo } from "../hooks/useSeo";

const DatenschutzPage = () => {
  useSeo({ title: "Datenschutzerklärung | JS Blond Salon", description: "Datenschutzerklärung der JS Blond Salon UG: Informationen zur Verarbeitung personenbezogener Daten auf js-blondsalon.de.", path: "/datenschutz" });
  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      <Navigation />
      
      {/* Hero */}
      <section className="pt-32 pb-8 bg-white">
        <div className="container-padding">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1
              className="text-4xl md:text-5xl font-normal tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="datenschutz-title"
            >
              Datenschutzerklärung
            </h1>
            <p className="mt-4 text-[#2C2C2C]/60">
              Informationen gemäß DSGVO Art. 13/14
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-[#F9F7F2]">
        <div className="container-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-3xl space-y-8"
            data-testid="datenschutz-content"
          >
            {/* Verantwortlicher */}
            <div className="bg-white p-8 border border-[#E6E2D6]">
              <h2
                className="text-xl font-medium mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                1. Verantwortlicher
              </h2>
              <div className="space-y-2 text-[#2C2C2C]/80 text-sm leading-relaxed">
                <p>
                  Verantwortlich für die Datenverarbeitung auf dieser Website ist:
                </p>
                <p className="font-medium mt-3">JS Blond Salon UG (haftungsbeschränkt)</p>
                <p>Daimlerstr. 26</p>
                <p>74211 Leingarten</p>
                <p className="mt-2">E-Mail: info@js-blondsalon.de</p>
                <p>Geschäftsführer: Fabian Sailer</p>
              </div>
            </div>

            {/* Überblick */}
            <div className="bg-white p-8 border border-[#E6E2D6]">
              <h2
                className="text-xl font-medium mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                2. Überblick der Datenverarbeitung
              </h2>
              <div className="space-y-3 text-[#2C2C2C]/80 text-sm leading-relaxed">
                <p>
                  Wir erheben und verarbeiten personenbezogene Daten nur, soweit dies für die 
                  Bereitstellung einer funktionsfähigen Website sowie unserer Inhalte und 
                  Leistungen erforderlich ist.
                </p>
                <p>
                  Die Verarbeitung personenbezogener Daten erfolgt regelmäßig nur nach 
                  Einwilligung des Nutzers oder wenn die Verarbeitung durch gesetzliche 
                  Vorschriften gestattet ist.
                </p>
              </div>
            </div>

            {/* Hosting */}
            <div className="bg-white p-8 border border-[#E6E2D6]">
              <h2
                className="text-xl font-medium mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                3. Hosting und Server-Logs
              </h2>
              <div className="space-y-3 text-[#2C2C2C]/80 text-sm leading-relaxed">
                <p>
                  Unser Hoster erhebt in sog. Logfiles folgende Daten, die Ihr Browser übermittelt:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>IP-Adresse</li>
                  <li>Adresse der vorher besuchten Website (Referer)</li>
                  <li>Datum und Uhrzeit der Anfrage</li>
                  <li>Zeitzonendifferenz zur Greenwich Mean Time</li>
                  <li>Inhalt der Anforderung</li>
                  <li>HTTP-Statuscode</li>
                  <li>Übertragene Datenmenge</li>
                  <li>Browser und Betriebssystem</li>
                </ul>
                <p className="mt-3">
                  <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse 
                  an der Sicherheit und Stabilität unserer Website).
                </p>
              </div>
            </div>

            {/* Kontaktformular */}
            <div className="bg-white p-8 border border-[#E6E2D6]">
              <h2
                className="text-xl font-medium mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                4. Kontaktformular
              </h2>
              <div className="space-y-3 text-[#2C2C2C]/80 text-sm leading-relaxed">
                <p>
                  Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben 
                  aus dem Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten 
                  (Name, E-Mail-Adresse, Nachricht) zwecks Bearbeitung der Anfrage und für den 
                  Fall von Anschlussfragen bei uns gespeichert.
                </p>
                <p>
                  <strong>Erhobene Daten:</strong> Name, E-Mail-Adresse, Nachrichteninhalt
                </p>
                <p>
                  <strong>Zweck:</strong> Bearbeitung Ihrer Anfrage und Kontaktaufnahme
                </p>
                <p>
                  <strong>Speicherdauer:</strong> Bis zur vollständigen Bearbeitung Ihrer Anfrage, 
                  maximal 2 Jahre nach letztem Kontakt
                </p>
                <p>
                  <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragsanbahnung) 
                  bzw. Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse)
                </p>
              </div>
            </div>

            {/* Cookies */}
            <div className="bg-white p-8 border border-[#E6E2D6]">
              <h2
                className="text-xl font-medium mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                5. Cookies
              </h2>
              <div className="space-y-3 text-[#2C2C2C]/80 text-sm leading-relaxed">
                <p>
                  Unsere Website verwendet Cookies. Cookies sind kleine Textdateien, die auf 
                  Ihrem Endgerät gespeichert werden und die Ihr Browser speichert.
                </p>
                <p><strong>Notwendige Cookies:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Cookie-Einwilligung (speichert Ihre Auswahl)</li>
                  <li>Session-Cookies (für die Funktion der Website)</li>
                </ul>
                <p className="mt-3">
                  <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung) für 
                  optionale Cookies, Art. 6 Abs. 1 lit. f DSGVO für technisch notwendige Cookies.
                </p>
                <p>
                  Sie können Ihren Browser so einstellen, dass Sie über das Setzen von Cookies 
                  informiert werden und einzeln über deren Annahme entscheiden.
                </p>
              </div>
            </div>

            {/* Instagram */}
            <div className="bg-white p-8 border border-[#E6E2D6]">
              <h2
                className="text-xl font-medium mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                6. Instagram Integration
              </h2>
              <div className="space-y-3 text-[#2C2C2C]/80 text-sm leading-relaxed">
                <p>
                  Auf unserer Website sind Funktionen des Dienstes Instagram eingebunden. 
                  Diese Funktionen werden angeboten durch:
                </p>
                <p className="font-medium">
                  Meta Platforms Ireland Limited<br />
                  4 Grand Canal Square, Grand Canal Harbour<br />
                  Dublin 2, Irland
                </p>
                <p>
                  Wenn Sie auf unserer Website Instagram-Inhalte anzeigen, wird eine Verbindung 
                  zu den Servern von Instagram hergestellt. Dabei wird dem Instagram-Server 
                  mitgeteilt, welche unserer Seiten Sie besucht haben.
                </p>
                <p>
                  Wenn Sie in Ihrem Instagram-Account eingeloggt sind, kann Instagram den Besuch 
                  unserer Website Ihrem Profil zuordnen.
                </p>
                <p>
                  <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO (berechtigtes 
                  Interesse an der Darstellung unserer Social-Media-Präsenz)
                </p>
                <p>
                  Weitere Informationen: 
                  <a href="https://help.instagram.com/519522125107875" target="_blank" rel="noopener noreferrer" className="text-[#9A8C73] hover:underline ml-1">
                    Instagram Datenschutzrichtlinie
                  </a>
                </p>
              </div>
            </div>

            {/* WhatsApp */}
            <div className="bg-white p-8 border border-[#E6E2D6]">
              <h2
                className="text-xl font-medium mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                7. WhatsApp Kontakt
              </h2>
              <div className="space-y-3 text-[#2C2C2C]/80 text-sm leading-relaxed">
                <p>
                  Wir bieten Ihnen die Möglichkeit, uns über WhatsApp zu kontaktieren. 
                  WhatsApp ist ein Dienst der:
                </p>
                <p className="font-medium">
                  WhatsApp Ireland Limited<br />
                  4 Grand Canal Square, Grand Canal Harbour<br />
                  Dublin 2, Irland
                </p>
                <p>
                  Wenn Sie uns über WhatsApp kontaktieren, werden Ihre Nachricht und die damit 
                  verbundenen Metadaten (z.B. Zeitstempel, Telefonnummer) über die Server von 
                  WhatsApp übertragen.
                </p>
                <p>
                  <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung durch 
                  aktive Kontaktaufnahme)
                </p>
              </div>
            </div>

            {/* Fresha Booking */}
            <div className="bg-white p-8 border border-[#E6E2D6]">
              <h2
                className="text-xl font-medium mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                8. Online-Buchungssystem (Fresha)
              </h2>
              <div className="space-y-3 text-[#2C2C2C]/80 text-sm leading-relaxed">
                <p>
                  Für Online-Terminbuchungen nutzen wir den Dienst Fresha. Wenn Sie auf den 
                  Buchungslink klicken, werden Sie auf die Website von Fresha weitergeleitet.
                </p>
                <p className="font-medium">
                  Fresha.com SV Ltd<br />
                  71-75 Shelton Street<br />
                  London, WC2H 9JQ, UK
                </p>
                <p>
                  Die Datenverarbeitung bei Fresha unterliegt deren eigenen Datenschutzbestimmungen.
                </p>
                <p>
                  Weitere Informationen: 
                  <a href="https://www.fresha.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#9A8C73] hover:underline ml-1">
                    Fresha Datenschutz
                  </a>
                </p>
              </div>
            </div>

            {/* Betroffenenrechte */}
            <div className="bg-white p-8 border border-[#E6E2D6]">
              <h2
                className="text-xl font-medium mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                9. Ihre Rechte (Betroffenenrechte)
              </h2>
              <div className="space-y-3 text-[#2C2C2C]/80 text-sm leading-relaxed">
                <p>Sie haben gegenüber uns folgende Rechte hinsichtlich der Sie betreffenden personenbezogenen Daten:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Auskunftsrecht (Art. 15 DSGVO):</strong> Sie können Auskunft über Ihre von uns verarbeiteten personenbezogenen Daten verlangen.</li>
                  <li><strong>Berichtigungsrecht (Art. 16 DSGVO):</strong> Sie können die Berichtigung unrichtiger oder die Vervollständigung Ihrer bei uns gespeicherten Daten verlangen.</li>
                  <li><strong>Löschungsrecht (Art. 17 DSGVO):</strong> Sie können die Löschung Ihrer bei uns gespeicherten Daten verlangen.</li>
                  <li><strong>Einschränkung der Verarbeitung (Art. 18 DSGVO):</strong> Sie können die Einschränkung der Verarbeitung Ihrer Daten verlangen.</li>
                  <li><strong>Datenübertragbarkeit (Art. 20 DSGVO):</strong> Sie können verlangen, dass wir Ihnen Ihre Daten in einem strukturierten Format übermitteln.</li>
                  <li><strong>Widerspruchsrecht (Art. 21 DSGVO):</strong> Sie können der Verarbeitung Ihrer Daten jederzeit widersprechen.</li>
                  <li><strong>Widerruf der Einwilligung (Art. 7 Abs. 3 DSGVO):</strong> Sie können Ihre erteilte Einwilligung jederzeit widerrufen.</li>
                </ul>
                <p className="mt-4">
                  Zur Ausübung Ihrer Rechte wenden Sie sich bitte an: <strong>info@js-blondsalon.de</strong>
                </p>
              </div>
            </div>

            {/* Beschwerderecht */}
            <div className="bg-white p-8 border border-[#E6E2D6]">
              <h2
                className="text-xl font-medium mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                10. Beschwerderecht bei der Aufsichtsbehörde
              </h2>
              <div className="space-y-3 text-[#2C2C2C]/80 text-sm leading-relaxed">
                <p>
                  Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde über die 
                  Verarbeitung Ihrer personenbezogenen Daten zu beschweren.
                </p>
                <p>
                  <strong>Zuständige Aufsichtsbehörde:</strong>
                </p>
                <p>
                  Der Landesbeauftragte für den Datenschutz und die Informationsfreiheit Baden-Württemberg<br />
                  Lautenschlagerstraße 20<br />
                  70173 Stuttgart<br />
                  Tel.: 0711/61 55 41-0<br />
                  E-Mail: poststelle@lfdi.bwl.de
                </p>
              </div>
            </div>

            {/* Aktualität */}
            <div className="bg-white p-8 border border-[#E6E2D6]">
              <h2
                className="text-xl font-medium mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                11. Aktualität und Änderung dieser Datenschutzerklärung
              </h2>
              <div className="space-y-3 text-[#2C2C2C]/80 text-sm leading-relaxed">
                <p>
                  Diese Datenschutzerklärung ist aktuell gültig und hat den Stand Februar 2025.
                </p>
                <p>
                  Durch die Weiterentwicklung unserer Website oder aufgrund geänderter gesetzlicher 
                  bzw. behördlicher Vorgaben kann es notwendig werden, diese Datenschutzerklärung 
                  zu ändern.
                </p>
              </div>
            </div>

            {/* Stand */}
            <div className="text-center text-[#2C2C2C]/40 text-sm pt-4">
              Stand: Februar 2025
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DatenschutzPage;
